const BASE = import.meta.env?.VITE_API_BASE ?? '/api';
// 2026-06-04: production-grade retry layer.
// Transient failures (browser network errors, 502/503/504/408/429) retry up to
// 3 additional attempts with exponential backoff (200ms, 600ms, 1.8s).
// Banner only fires after ALL attempts exhaust — so a momentary network blip
// no longer slaps a red FATAL banner across the screen.
//
// POST/PUT/PATCH/DELETE NEVER retry (would risk duplicating mutations).
// 4xx never retry (caller-handled validation errors).
// Banner auto-clears on the first successful API call after recovery.
import { errorBus } from './errorBus';
const TRANSIENT_STATUS = new Set([502, 503, 504, 408, 429]);
const MAX_RETRIES = 3;
const BACKOFF_MS = [200, 600, 1800];
const IDEMPOTENT_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);
const NETWORK_DOWN_KEY = 'network::down';
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
function isTransientNetworkError(err) {
    // Browser fetch network failures throw TypeError('Failed to fetch').
    // AbortError = intentional cancellation, never retry.
    const name = String(err?.name ?? '');
    if (name === 'AbortError')
        return false;
    if (name === 'TypeError')
        return true;
    return /Failed to fetch|NetworkError|ECONN|network/i.test(String(err?.message ?? err));
}
async function handle(res, urlForBanner) {
    if (!res.ok) {
        let body = null;
        try {
            body = await res.json();
        }
        catch { }
        const msg = body?.message ?? body?.error ?? res.statusText;
        if (res.status >= 500) {
            errorBus.push({
                severity: 'fatal',
                title: 'Backend error ' + res.status + ' on ' + (urlForBanner ?? 'API call'),
                detail: typeof body === 'object' ? JSON.stringify(body, null, 2) : String(msg),
                source: 'api',
            });
        }
        throw new Error(res.status + ' ' + msg);
    }
    if (res.status === 204)
        return null;
    return res.json();
}
async function fetchWithRetry(path, init) {
    const method = (init?.method ?? 'GET').toUpperCase();
    const canRetry = IDEMPOTENT_METHODS.has(method);
    let lastErr;
    let lastTransientStatus = null;
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        try {
            const res = await fetch(BASE + path, init);
            // Transient HTTP status — retry if idempotent and we have retries left
            if (TRANSIENT_STATUS.has(res.status) && canRetry && attempt < MAX_RETRIES) {
                lastTransientStatus = res.status;
                await sleep(BACKOFF_MS[attempt]);
                continue;
            }
            // Success — clear any stale 'network::down' banner from prior failures
            if (attempt > 0) {
                errorBus.dismissByKey(NETWORK_DOWN_KEY);
            }
            return await handle(res, path);
        }
        catch (err) {
            lastErr = err;
            // Non-transient (handle() threw on 4xx/5xx) → bubble immediately
            if (!isTransientNetworkError(err) || !canRetry || attempt >= MAX_RETRIES) {
                break;
            }
            await sleep(BACKOFF_MS[attempt]);
        }
    }
    // Only NOW does the banner fire — all retries exhausted on transient failure
    if (lastErr && isTransientNetworkError(lastErr)) {
        errorBus.push({
            severity: 'fatal',
            title: 'Lost connection to backend',
            detail: 'Path: ' + path + '\nFailed after ' + (MAX_RETRIES + 1) + ' attempts.\nLast error: ' + (lastErr?.message ?? String(lastErr)),
            source: 'network',
            dedupeKey: NETWORK_DOWN_KEY,
        });
        throw lastErr;
    }
    if (lastTransientStatus) {
        throw new Error(lastTransientStatus + ' (after ' + (MAX_RETRIES + 1) + ' attempts)');
    }
    throw lastErr;
}
export const api = {
    get: (path) => fetchWithRetry(path, { credentials: 'include' }),
    post: (path, body) => fetchWithRetry(path, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
    }),
    put: (path, body) => fetchWithRetry(path, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
    }),
    patch: (path, body) => fetchWithRetry(path, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
    }),
    del: (path) => fetchWithRetry(path, { method: 'DELETE', credentials: 'include' }),
};
